// File: /frontend/src/pages/user/HomePage.js
// ZAMMER — Mobile-first unified homepage (KNOT-style PWA layout)

import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { getMarketplaceProducts } from '../../services/productService';
import { getBanners } from '../../services/bannerService';
import { getPromoBanners } from '../../services/promoBannerService';
import UserLayout from '../../components/layouts/UserLayout';
import UserHeader from '../../components/header/UserHeader';
import CircularCategorySelector from '../../components/user/CircularCategorySelector';
import OfferCardsRow from '../../components/user/OfferCardsRow';
import PromoBannerCarousel from '../../components/common/PromoBannerCarousel';
import OccasionCircles from '../../components/user/OccasionCircles';
import CategoryBannerStrip from '../../components/user/CategoryBannerStrip';
import Level2BannerGrid from '../../components/user/Level2BannerGrid';
// TEMPORARILY DISABLED — these brands (Gucci, Louis Vuitton, Nike, adidas, Zara,
// H&M, Supreme, Off-White, Tommy Hilfiger, Puma, Calvin Klein, Uniqlo) are NOT
// onboarded yet, so we must not show their logos. The component is intentionally
// kept intact at components/user/BrandDiscoverGrid.js — re-enable by uncommenting
// this import and the "Discover Brands" block below once the brands are live.
// import BrandDiscoverGrid from '../../components/user/BrandDiscoverGrid';
import CategoryShowcase from '../../components/user/CategoryShowcase';
import DiscoveryFeed from '../../components/user/DiscoveryFeed';
import ProductCard from '../../components/common/ProductCard';
import UserFooter from '../../components/user/UserFooter';
import { useAddToCart } from '../../hooks/useAddToCart';
import { ProductGridSkeleton } from '../../components/common/SkeletonLoader';
import { ChevronRight } from 'lucide-react';

const GENDER_MAP = {
  'Men Fashion': 'men',
  'Women Fashion': 'women',
  'Kids Fashion': 'kids',
};

const HomePage = () => {
  const { userAuth } = useContext(AuthContext);
  const isMountedRef = useRef(true);
  const { addingToCart, handleAddToCart } = useAddToCart();

  // Gender-based selection
  const [selectedLevel1, setSelectedLevel1] = useState(() => {
    const gender = userAuth?.user?.gender;
    if (gender === 'Female') return 'Women Fashion';
    if (gender === 'Male') return 'Men Fashion';
    return 'Men Fashion';
  });

  // Data states
  const [promoBanners, setPromoBanners] = useState([]);
  const [level2Banners, setLevel2Banners] = useState([]);
  const [level3Banners, setLevel3Banners] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingTrending, setLoadingTrending] = useState(true);

  // Fetch promo banners (once)
  useEffect(() => {
    isMountedRef.current = true;
    const fetchPromos = async () => {
      try {
        const res = await getPromoBanners({ page: 'homepage' });
        if (res.success && isMountedRef.current) setPromoBanners(res.data || []);
      } catch (e) { console.error('Promo banners error:', e); }
    };
    fetchPromos();
    return () => { isMountedRef.current = false; };
  }, []);

  // Fetch gender-dependent data whenever gender toggle changes
  const fetchGenderData = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const [l2Res, l3Res, productsRes, trendingRes] = await Promise.all([
        // Only categories that actually hold active products come back — every
        // banner also carries `productCount`. Tiles for empty categories used
        // to land the buyer on "No Products Found" (46% of L2, 68% of L3).
        // Nothing to filter here, so a section left with no categories is an
        // empty array and its `.length > 0` guard below hides the whole block.
        getBanners({ level: 2, categoryLevel1: selectedLevel1 }),
        getBanners({ level: 3, categoryLevel1: selectedLevel1 }),
        // New Arrivals must actually be newest-first. Without an explicit sort
        // this returned the backend's default order, so it was neither "new"
        // nor different from the Trending rail below it.
        getMarketplaceProducts({
          limit: 8,
          categoryLevel1: selectedLevel1,
          status: 'active',
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }),
        // Trending is ranked by rating/review volume, so the two rails surface
        // genuinely different products even when few items carry isTrending.
        getMarketplaceProducts({
          limit: 8,
          isTrending: true,
          categoryLevel1: selectedLevel1,
          status: 'active',
          sortBy: 'popularity'
        }),
      ]);

      if (!isMountedRef.current) return;

      if (l2Res.success) setLevel2Banners(l2Res.data || []);
      if (l3Res.success) setLevel3Banners(l3Res.data || []);
      if (productsRes.success) setNewArrivals(productsRes.data || []);
      setLoadingProducts(false);
      if (trendingRes.success) setTrendingProducts(trendingRes.data || []);
      setLoadingTrending(false);
    } catch (e) {
      console.error('Gender data fetch error:', e);
      setLoadingProducts(false);
      setLoadingTrending(false);
    }
  }, [selectedLevel1]);

  useEffect(() => {
    setLoadingProducts(true);
    setLoadingTrending(true);
    fetchGenderData();
  }, [fetchGenderData]);

  // Gender-filtered promo banners
  const genderParam = GENDER_MAP[selectedLevel1] || 'men';
  const filteredPromos = promoBanners.filter(
    (b) => b.targetGender === 'all' || b.targetGender === genderParam
  );

  // Partition promos so each banner appears in exactly one section:
  //   - banners with `discountText` belong to the compact offer-card strip
  //   - banners without it belong to the full-width hero carousel
  // Eliminates the visible duplication where the same banner appeared in both.
  const offerCardBanners = filteredPromos.filter(
    (b) => b.discountText && b.discountText.trim() !== ''
  );
  const heroBanners = filteredPromos.filter(
    (b) => !b.discountText || b.discountText.trim() === ''
  );
  // Fallback so the hero never goes empty when only offer-style banners exist.
  const heroBannersResolved = heroBanners.length > 0 ? heroBanners : offerCardBanners;

  return (
    <UserLayout>
      <div className="bg-white">
        {/* Sticky Header */}
        <UserHeader />

        {/* Gender Toggle — MAN / WOMAN / KIDS */}
        <CircularCategorySelector
          selectedCategory={selectedLevel1}
          onSelectCategory={setSelectedLevel1}
        />

        {/* Offer Cards Row — horizontal scroll of discount-only banners */}
        {offerCardBanners.length > 0 && (
          <OfferCardsRow banners={offerCardBanners} />
        )}

        {/* Promotional Banner Carousel — full-width auto-rotating hero
            (lookbook banners only — discount banners live in the strip above) */}
        {heroBannersResolved.length > 0 && (
          <div className="mb-2">
            <PromoBannerCarousel banners={heroBannersResolved} />
          </div>
        )}

        {/* Category Circles — L2 banners as rounded squares */}
        <OccasionCircles banners={level2Banners} level1Category={selectedLevel1} />

        {/* Category Banner Strip — L3 banners as full-width scrollable cards */}
        {level3Banners.length > 0 && (
          <CategoryBannerStrip banners={level3Banners.slice(0, 5)} />
        )}

        {/* Shop by Category — L3 banners in a 4-col grid.
            This used to render `level2Banners`, the same array OccasionCircles
            shows above it, so the page displayed the identical five categories
            twice under two headings. Showing L3 here makes the section
            genuinely different and surfaces more of the catalogue. */}
        {level3Banners.length > 0 && (
          <div className="px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">
                Shop by Category
              </h3>
              <Link
                to={`/user/browse/${encodeURIComponent(selectedLevel1)}`}
                className="text-blue-500 text-xs font-semibold flex items-center gap-0.5"
              >
                See all <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
            <Level2BannerGrid
              banners={level3Banners.slice(0, 8)}
              level1Category={selectedLevel1}
              level={3}
            />
          </div>
        )}

        {/* Discover Brands — HIDDEN: these brands are not onboarded yet, so we
            must not display their logos. Code retained (not deleted); uncomment
            this block AND the BrandDiscoverGrid import at the top to restore.
        <div className="px-4 py-4 border-t border-gray-50">
          <BrandDiscoverGrid />
        </div>
        */}

        {/* New Arrivals — 2-col product grid */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-900">New Arrivals</h3>
            <Link
              to={`/user/products?categoryLevel1=${encodeURIComponent(selectedLevel1)}`}
              className="text-blue-500 text-xs font-semibold flex items-center gap-0.5"
            >
              View all <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
          {loadingProducts ? (
            <ProductGridSkeleton count={4} cols="grid-cols-2" />
          ) : newArrivals.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {newArrivals.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  isAddingToCart={addingToCart[product._id]}
                  showAddToCart={false}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">No products yet. Check back soon!</p>
            </div>
          )}
        </div>

        {/* Second banner strip between product sections */}
        {level3Banners.length > 2 && (
          <CategoryBannerStrip banners={level3Banners.slice(2, 5)} />
        )}

        {/* Trending Now — 2-col product grid */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-900">Trending Now</h3>
            <Link
              to="/user/trending"
              className="text-blue-500 text-xs font-semibold flex items-center gap-0.5"
            >
              View all <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
          {loadingTrending ? (
            <ProductGridSkeleton count={4} cols="grid-cols-2" />
          ) : trendingProducts.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {trendingProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  isAddingToCart={addingToCart[product._id]}
                  showAddToCart={false}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">No trending products right now</p>
            </div>
          )}
        </div>

        {/* Category Showcase — L4 banner rows */}
        <div className="py-2">
          <CategoryShowcase selectedLevel1={selectedLevel1} />
        </div>

        {/* Discovery feed — infinite scroll over everything in the selected
            Level-1. The curated rails above surface ~16 products; this exposes
            the rest with every leaf category interleaved, so nothing is
            unreachable from the homepage. It takes the gender toggle too:
            unscoped, the 76% Women catalogue meant picking "Men" still gave a
            feed of womenswear. */}
        <DiscoveryFeed
          onAddToCart={handleAddToCart}
          addingToCart={addingToCart}
          categoryLevel1={selectedLevel1}
        />

        {/* Footer */}
        <UserFooter />
      </div>
    </UserLayout>
  );
};

export default HomePage;
